import { useState } from "react";
import { ChecklistStep } from "../../components/widgets/ProgressBar";
import ChoiceGroup from "../../components/forms/ChoiceGroup";
import CheckboxGroup from "../../components/forms/CheckboxGroup";
import NotesField from "../../components/forms/NotesField";
import { useStore } from "../../js/state/store";

const PRIORITY = [
  { value: "required", label: "Required" },
  { value: "nice", label: "Nice to have" },
  { value: "not-needed", label: "Not needed" },
];

export default function BuyerAccessibility() {
  const saved = useStore().state.buyer.current.preferences.accessibility || {};
  const [form, setForm] = useState({
    noStepEntry: saved.noStepEntry || "",
    wideHallways: saved.wideHallways || "",
    walkInShower: saved.walkInShower || "",
    rampAccess: saved.rampAccess || "",
    adaKitchen: saved.adaKitchen || "",
    adaBathroom: saved.adaBathroom || "",
    mobilityNeeds: saved.mobilityNeeds || [],
    notes: saved.notes || "",
  });

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  return (
    <ChecklistStep
      title="Accessibility"
      lead="Capture mobility and access needs clearly so your agent can screen homes — today and as life changes."
      preferenceKey="accessibility"
      value={form}
    >
      <section className="checklist-section">
        <h2>Core Access Features</h2>
        <p className="section-help">Mark what is required versus optional. Your agent can treat “required” as a filter.</p>
        <ChoiceGroup label="No-step entry" name="noStepEntry" value={form.noStepEntry} onChange={update} options={PRIORITY} />
        <ChoiceGroup label="Wide hallways" name="wideHallways" value={form.wideHallways} onChange={update} options={PRIORITY} />
        <ChoiceGroup label="Walk-in shower" name="walkInShower" value={form.walkInShower} onChange={update} options={PRIORITY} />
        <ChoiceGroup label="Ramp access" name="rampAccess" value={form.rampAccess} onChange={update} options={PRIORITY} />
        <ChoiceGroup
          label="ADA-friendly kitchen features"
          name="adaKitchen"
          value={form.adaKitchen}
          onChange={update}
          hint="Lower counters, knee space, reachable storage, lever hardware."
          options={PRIORITY}
        />
        <ChoiceGroup
          label="ADA-friendly bathroom features"
          name="adaBathroom"
          value={form.adaBathroom}
          onChange={update}
          hint="Blocking for grab bars, roll-under sink, comfort-height toilet, curbless shower."
          options={PRIORITY}
        />
      </section>

      <section className="checklist-section">
        <h2>Additional Mobility Needs</h2>
        <CheckboxGroup
          label="Also important"
          name="mobilityNeeds"
          values={form.mobilityNeeds}
          onChange={update}
          layout="grid"
          options={[
            { value: "main-floor-bedroom", label: "Bedroom on the main floor" },
            { value: "main-floor-bath", label: "Full bath on the main floor" },
            { value: "lever-handles", label: "Lever door handles" },
            { value: "wide-doors", label: "36-inch doors" },
            { value: "elevator", label: "Elevator or stack space" },
            { value: "single-level", label: "Single-level living" },
            { value: "step-free-shower", label: "Curbless / zero-threshold shower" },
            { value: "future-adapt", label: "Easy to adapt later (aging in place)" },
          ]}
        />
        <NotesField
          label="Accessibility notes for my agent"
          name="notes"
          value={form.notes}
          onChange={update}
          placeholder="Equipment, turning radius, caregiver space, or features a listing photo might miss."
        />
      </section>
    </ChecklistStep>
  );
}
